import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'
import { isBinaryFile } from '~/utils/tarUtils'

// ============================================
// Canvas Content Read/Write Types
// ============================================

export interface CanvasSaveRequest {
  s3Path: string
  content: string
  contentType: string
}

export interface CanvasSaveResponse {
  success: boolean
  s3Path: string
  updatedAt: string
}

export interface CanvasReadResponse {
  content: string
  contentType: string
}

interface CanvasReadApiResponse {
  content: string
  content_type: string
}

/**
 * Read editable canvas content from S3.
 *
 * Used for editable canvas items to load content into the TipTap editor.
 * Returns the file content as HTML string.
 *
 * @param workspaceUuid - The workspace UUID
 * @param s3Path - The S3 path of the canvas file
 * @returns Promise with content and content type
 */
export async function readEditableCanvasContent(
  workspaceUuid: string,
  s3Path: string
): Promise<CanvasReadResponse> {
  const encodedPath = encodeURIComponent(s3Path)
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/canvas/content?s3_path=${encodedPath}`,
    {
      method: 'GET'
    }
  )

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to read canvas content'))
  }

  const data = (await response.json()) as Partial<CanvasReadApiResponse>
  if (typeof data.content !== 'string' || typeof data.content_type !== 'string') {
    throw new Error('Invalid canvas read response')
  }

  return {
    content: data.content,
    contentType: data.content_type
  }
}

/**
 * Save edited canvas content to S3.
 *
 * Used for editable canvas items to save content from the TipTap editor.
 * Overwrites the existing file with the new content.
 *
 * @param workspaceUuid - The workspace UUID
 * @param request - The save request containing s3Path, content, and contentType
 * @returns Promise with save response
 */
export async function saveCanvasContent(
  workspaceUuid: string,
  request: CanvasSaveRequest
): Promise<CanvasSaveResponse> {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/canvas/content`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      s3_path: request.s3Path,
      content: request.content,
      content_type: request.contentType
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to save canvas content'))
  }

  return response.json()
}

// ============================================
// Canvas Read Types
// ============================================

/**
 * Get a presigned download URL for a canvas artifact file stored in S3.
 *
 * Canvas artifacts are files generated by agents during task execution,
 * stored in conv_scratch_storage/ with UUID-based path structure.
 *
 * @param workspaceUuid - The workspace UUID
 * @param s3Path - The S3 path of the canvas file (conv_scratch_storage/...)
 * @returns Object containing the presigned URL and filename
 */
export async function getCanvasDownloadUrl(
  workspaceUuid: string,
  s3Path: string
): Promise<{ url: string; filename: string }> {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/canvas/download-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ s3_path: s3Path })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to get canvas download URL'))
  }

  return response.json()
}

/**
 * Convert blob to base64 string (without data URI prefix)
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const dataUri = reader.result as string
      // Strip the data URI prefix to get raw base64
      // Format: "data:image/png;base64,iVBORw0..." -> "iVBORw0..."
      const base64 = dataUri.includes(',') ? dataUri.split(',')[1] : dataUri
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Fetch canvas file content from S3.
 *
 * Only used for CANVAS_TYPE_S3_FILE - images and markdown have inline content.
 * Returns base64 for binary files (tar.gz, images, etc.) or text for code files.
 *
 * @param workspaceUuid - The workspace UUID
 * @param s3Path - The S3 path of the canvas file
 * @returns The file content as string (text for code, raw base64 for binaries)
 */
export async function getCanvasFileContent(workspaceUuid: string, s3Path: string): Promise<string> {
  const { url, filename } = await getCanvasDownloadUrl(workspaceUuid, s3Path)

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to download canvas file: ${response.statusText}`)
  }

  // Check extension to determine if binary
  if (isBinaryFile(filename)) {
    const blob = await response.blob()
    return blobToBase64(blob)
  }

  return response.text()
}
